import { useEffect, useRef } from 'react';
import { ReadCampaign } from '@tinct/core-api-models';
import { GENERATION_QUOTA_EXCEEDED_ERROR_CODE, useRegenerateOutdated } from '@tinct/core-api';
import { useGetCurrentQuotas, useGetCurrentWorkspaceId } from '@tinct/core-workspace';
import { useOpenPaywall } from '@tinct/web-feature-payment';

type OutdatedCampaign = Pick<ReadCampaign, 'id' | 'outdatedStatus' | 'pagesToRegenerateCount'>;

/**
 * Campaigns with a regeneration call in flight, shared across every mounted instance so two
 * surfaces showing the same campaign (the studio host and its settings panel, say) fire once.
 */
const inFlight = new Set<string>();

const isQuotaRefusal = (error: unknown) =>
  (error as { errorCode?: number } | null)?.errorCode === GENERATION_QUOTA_EXCEEDED_ERROR_CODE;

/**
 * The automatic half of the single "Outdated" badge. A change to a generation input (campaign
 * context, landing page, block list, a chat edit applied to all pages, ...) flags the generated
 * pages `needs_regeneration`; the backend never regenerates on its own because that spends
 * credits, so whoever loads the campaign next starts the regeneration — or, when the workspace
 * cannot cover `pagesToRegenerateCount` credits, gets the paywall instead. Pages then advance to
 * `needs_republication` (published) or back in sync on their own.
 *
 * The campaign must project `outdatedStatus` and `pagesToRegenerateCount`. A given
 * (campaign, count, credits) situation is acted on once per mount: the count drops to zero as
 * soon as the jobs exist (in-flight pages are not counted), and a paywall dismissed without buying
 * credits is not reopened on every refetch. Buying credits changes the situation and retries.
 */
export function useAutoRegenerateOutdatedPages(campaign: OutdatedCampaign | null | undefined) {
  const workspaceId = useGetCurrentWorkspaceId();
  const { lpGenerationQuota } = useGetCurrentQuotas();
  const { openPaywall } = useOpenPaywall();
  const { mutateAsync: regenerateOutdated } = useRegenerateOutdated();
  const handledRef = useRef<string | null>(null);

  const campaignId = campaign?.id ?? null;
  const count =
    campaign?.outdatedStatus === 'needs_regeneration' ? (campaign.pagesToRegenerateCount ?? 0) : 0;
  // A missing quota reads as no credits, like the generate-all gate.
  const hasCredits = (lpGenerationQuota?.remainingQuota ?? 0) >= count;

  useEffect(() => {
    if (!campaignId || count <= 0) {
      handledRef.current = null;
      return;
    }
    const situation = `${campaignId}:${count}:${hasCredits}`;
    if (handledRef.current === situation || inFlight.has(campaignId)) return;
    handledRef.current = situation;

    if (!hasCredits) {
      openPaywall('not_enough_credits');
      return;
    }

    inFlight.add(campaignId);
    regenerateOutdated({ id: campaignId, organizationId: workspaceId })
      .catch((error: unknown) => {
        // The backend re-checks the credits itself (the quota may have moved since the last
        // organization fetch); its refusal is the same paywall.
        if (isQuotaRefusal(error)) {
          openPaywall('not_enough_credits');
        } else {
          console.error('Error regenerating outdated pages:', error);
        }
      })
      .finally(() => {
        inFlight.delete(campaignId);
      });
  }, [campaignId, count, hasCredits, workspaceId, openPaywall, regenerateOutdated]);
}
